import React, { useCallback, useContext, useMemo, useState } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import MiniProductReviewsBlock from './MiniProductReviewsBlock';
import { Link } from 'react-router-dom';
import { CartContext } from './context/CartContext';
import { bool, func, number, oneOfType, string } from 'prop-types';
import ResponsiveImage from './ResponsiveImage';
import AppContext from './context/AppContext';
import { Button } from 'react-bootstrap';
import { IoClose } from 'react-icons/io5';
import { ToastContext } from './context/ToastContext';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';

export default function ProductListItem({ productId, title, vendor, price, imageUrl, handle, compareAtPrice, availableForSale, availableQuantity, bypassNegativeQuantity, inventoryLevelId, avgRating, totalReviews, minimized, productWidth, historyMode, useSlider, productCount, setProductCount, addToOrder, orderId }) {
    const { appContext } = useContext(AppContext);const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const { sendMessage } = useContext(ToastContext);
    const [deletedFromView, setDeletedFromView] = useState(false);
    const [isAddingToOrder, setIsAddingToOrder] = useState(false);
    const appConfig = appContext.config.themeConfig;
    const {
        cartState,
        cartUpdating,
        addLineItem,
        setShowCartOverlay,
    } = useContext(CartContext);
    // Add hints that the inventory level is exhausted
    // https://app.clickup.com/t/86a1dkgna
    const addedQuantity = useMemo(() => {
        if (!cartState) {
            return 0;
        }
        return cartState.filter((i) => i.inventoryLevelId === inventoryLevelId).at(0)?.quantity ?? 0;
    }, [cartState, inventoryLevelId]);

    const quantity = useMemo(() => !bypassNegativeQuantity ? parseInt(availableQuantity - addedQuantity) : Infinity, [bypassNegativeQuantity, availableQuantity, addedQuantity]);

    // Remove product from history
    const removeProductFromHistory = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!productId) {
            return;
        }
        // eslint-disable-next-line no-undef
        const productIds = localStorage.getItem('recentlyViewedProducts');
        if (!productIds) {
            return;
        }
        const productIdsArr = productIds.split(',');
        const newProductIds = productIdsArr.filter((id) => id !== String(productId));
        // eslint-disable-next-line no-undef
        localStorage.setItem('recentlyViewedProducts', newProductIds.join(','));
        setDeletedFromView(true);
        if (productCount > 0) {
            setProductCount(productCount - 1);
        }
    }, [productId, productCount, setProductCount]);

    // Add product to order
    const handleAddToOrder = async () => {
        if (!addToOrder || !orderId) {
            return;
        }
        setIsAddingToOrder(true);
        try {
            const response = await fetch(new URL(`/api/orders/${orderId}/relatedProducts/${productId}`, process.env.APP_URL), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                setIsAddingToOrder(false);
                return sendMessage('Failed to add product to order', 'error');
            }
            const data = await response.json();
            if (data.error) {
                setIsAddingToOrder(false);
                return sendMessage(data.error, 'error');
            }
            sendMessage('You\'ve added this product to your order. Expect hearing from us soon.', 'success');
        } catch (e) {
            setIsAddingToOrder(false);
            sendMessage(e.message, 'error');
        }
    };

    // Handle click event
    const handleClick = async (_e) => {
        if (!addToOrder && !orderId) {
            await addLineItem(inventoryLevelId, 1);
            setShowCartOverlay(true);
            return;
        }
        if (addToOrder && orderId) {
            handleAddToOrder();
        }
    };
    if (deletedFromView === true) {
        return null;
    }
    return (<li
        className={'product-list--item'}
        style={{
            ...(useSlider === true) && ({ flex: `1 0 ${productWidth}px` }),
            ...(useSlider === true) && ({ width: `${productWidth}px` }),
        }}
    >
        <Link
            to={`${productPath[0].pathBeforeParams}/${handle}`}>
            {historyMode === true && (
                <Button
                    className={'remove-history--btn'}
                    onClick={removeProductFromHistory}><IoClose
                        size={'50px'} /></Button>
            )}
            {imageUrl && (<figure
                className={'product-list-item--image'}
                style={{ ...minimized === true && ({ height: '150px' }) }}
                data-product-item-image>
                <ResponsiveImage
                    imageUrl={imageUrl}
                    imageAlt={title}
                    mobileSize={minimized ? '150px' : '350px'}
                    desktopSize={minimized ? '150px' : '350px'}></ResponsiveImage>
            </figure>)}
            {!imageUrl && (<figure
                className={'product-list-item--image'}
                style={{ ...minimized === true && ({ height: '150px' }) }}
                data-product-item-image>
                <ResponsiveImage
                    imageUrl={appConfig.productNoImageUrl}
                    imageAlt={'No product image'}
                    mobileSize={minimized ? '150px' : '350px'}
                    desktopSize={minimized ? '150px' : '350px'}></ResponsiveImage>
            </figure>)}
            {(availableForSale === false || availableForSale === 0) && (<span
                className={'product-list-item--badge sold-out'}>Sold out</span>)}
            {((availableForSale === true || availableForSale === 1) && !isNaN(compareAtPrice) && compareAtPrice > price) && (<span
                className={'product-list-item--badge compare-price'}>Save {getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice - price).toFixed(2)} {appConfig.storeCurrency}</span>)}
        </Link>
        <div
            className={'product-list-item--description'}>
            {!historyMode && (<>
                {(availableForSale === true || availableForSale === 1) && !isNaN(compareAtPrice) && compareAtPrice > price && (<span
                    className={'product-list-item--compare-price'}>{getSymbolFromCurrency(appConfig.storeCurrency)}{(compareAtPrice).toFixed(2)} {appConfig.storeCurrency}</span>)}
                <span
                    className={'product-list-item--price'}>
                    {(!isNaN(price) && price > 0) && (<>
                        {getSymbolFromCurrency(appConfig.storeCurrency)}{price} {appConfig.storeCurrency}
                    </>)}
                    {price === 0 && (<>
                        FREE
                    </>)}
                </span>
            </>)}
            <h2
                className={'product-list-item--title'}><Link
                    to={`${productPath[0].pathBeforeParams}/${handle}`}>{title}</Link></h2>
            {!historyMode && (<>
                {vendor && (<Link
                    to={`/searchresults.asp?v=${encodeURIComponent(vendor)}`}
                    className={'product-list-item--vendor'}>{vendor}</Link>)}
                <MiniProductReviewsBlock
                    avgRating={avgRating}
                    totalReviews={totalReviews} />
            </>)}
            <button
                className={'cart-add--btn'}
                disabled={(cartUpdating || !availableForSale || quantity <= 0 || isAddingToOrder)}
                style={{
                    ...(cartUpdating || !availableForSale || quantity <= 0 || isAddingToOrder) && ({
                        backgroundColor: 'lightgray',
                        borderColor: 'lightgray'
                    })
                }}
                onClick={handleClick}
            >
                {(addToOrder === true && orderId) && (<>Add to Order</>)}
                {(addToOrder === false || orderId === null) && (<>Add to Cart</>)}
            </button>
        </div>
    </li>);
}

ProductListItem.propTypes = {
    productId: oneOfType([string, number]),
    title: string,
    vendor: string,
    price: oneOfType([string, number]),
    imageUrl: string,
    handle: string,
    compareAtPrice: oneOfType([string, number]),
    availableForSale: oneOfType([bool, number]),
    availableQuantity: number,
    bypassNegativeQuantity: bool,
    inventoryLevelId: number,
    avgRating: oneOfType([string, number]),
    totalReviews: oneOfType([string, number]),
    minimized: bool,
    productWidth: oneOfType([string, number]),
    historyMode: bool,
    useSlider: bool,
    productCount: number,
    setProductCount: func,
    addToOrder: bool,
    orderId: number,
};

ProductListItem.defaultProps = {
    historyMode: false,
    useSlider: false,
    addToOrder: false,
    orderId: null,
};